<template>
  <div class="p-6">
    <h2 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-6">
      {{ location ? 'Edit Location' : 'New Location' }}
    </h2>

    <form @submit.prevent="handleSubmit" class="space-y-6">
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name *</label>
        <UiFormInput v-model="form.name" type="text" />
      </div>

      <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 pt-2">
        Address
      </h3>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="md:col-span-2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Address Line 1</label>
          <UiFormInput v-model="form.address_line_1" type="text" />
        </div>
        <div class="md:col-span-2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Address Line 2</label>
          <UiFormInput v-model="form.address_line_2" type="text" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">City</label>
          <UiFormInput v-model="form.city" type="text" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">State</label>
          <UiFormInput v-model="form.state" type="text" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">ZIP</label>
          <UiFormInput v-model="form.zip" type="text" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Country</label>
          <UiFormInput v-model="form.country" type="text" />
        </div>
      </div>

      <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 pt-2">
        Contact
      </h3>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
          <UiFormInput v-model="form.phone_number" type="phone" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
          <UiFormInput v-model="form.email" type="email" />
        </div>
        <div class="md:col-span-2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Website URL</label>
          <UiFormInput v-model="form.website_url" type="text" />
        </div>
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Place ID</label>
        <UiFormInput v-model="form.place_id" type="text" />
      </div>

      <div v-if="errors" class="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
        <p class="text-sm text-red-700 dark:text-red-400">
          {{ errors }}
        </p>
      </div>

      <div class="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          @click="$emit('on-close-panel')"
          class="px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
          Cancel
        </button>
        <button
          type="submit"
          :disabled="saving"
          class="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50">
          {{ saving ? 'Saving...' : (location ? 'Update' : 'Create') }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { createLocation, updateLocation } from '~/javascript/api/locations.js'
import { UiFormInput } from '~/javascript/components/ui/form/input'

const props = defineProps({
  location: { type: Object, default: null },
  businessId: { type: String, required: true }
})

const emit = defineEmits(['on-close-panel', 'saved'])

const saving = ref(false)
const errors = ref(null)

const defaultForm = {
  name: '',
  address_line_1: '',
  address_line_2: '',
  city: '',
  state: '',
  country: '',
  zip: '',
  phone_number: '',
  email: '',
  website_url: '',
  place_id: ''
}

const form = ref({ ...defaultForm })

const handleSubmit = async() => {
  try {
    saving.value = true
    errors.value = null

    if (props.location) {
      await updateLocation(props.location.id, { location: form.value })
    } else {
      await createLocation(props.businessId, { location: form.value })
    }
    emit('saved')
  } catch (error) {
    if (error.response?.data?.details) {
      errors.value = Object.values(error.response.data.details).flat().join(', ')
    } else {
      errors.value = 'An error occurred. Please try again.'
    }
  } finally {
    saving.value = false
  }
}

onMounted(() => {
  if (props.location) {
    Object.keys(defaultForm).forEach((key) => {
      form.value[key] = props.location[key] || ''
    })
  }
})
</script>
